import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';

import { Field, Form, Formik } from 'formik';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Flex,
  Box,
  Input,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react';

import { FaPlus } from 'react-icons/fa';

import { createAddCourseQuestAction } from 'store/modules/quest/quest-actions';
import { questActions } from 'store/modules/quest/quest-slice';

import { ReducerStatus } from 'utils/constants';

import ResultModal from 'components/common/modals/ResultModal';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import MultiEmailInput from 'components/common/forms/MultiEmailInput';

function CreateQuestMd({ id }) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const dispatch = useDispatch();

  const createAddQuest = useSelector((state) => state.quest.createAddQuest);
  const course = useSelector((state) => state.course.course);

  const [createFormInputs, setCreateFormInputs] = useState({
    name: '',
    description: null,
  });

  const questFormValidation = Yup.object({
    name: Yup.string().max(155).required('Required'),
    description: Yup.string().nullable().max(1000),
  });

  // State for managing selected users/groups in MultiEmailInput
  const [selectedEmails, setSelectedEmails] = useState([]);

  /**
   * Handles adding a new item (user or group) to the selected emails list
   */
  const handleEmailAdd = (item) => {
    setSelectedEmails((prev) => [...prev, item]);
  };

  /**
   * Handles removing an item from the selected emails list
   */
  const handleEmailRemove = (item) => {
    setSelectedEmails((prev) =>
      prev.filter(
        (email) => email.value !== item.value || email.type !== item.type
      )
    );
  };

  useEffect(() => {
    if (course && isOpen) {
      const parsedGroups = JSON.parse(course?.ownerGroups);
      if (
        Array.isArray(parsedGroups) &&
        parsedGroups.every((group) => typeof group === 'string')
      ) {
        // Convert existing emails format to MultiEmailInput format
        const convertedEmails = parsedGroups.map((email) => ({
          label: email,
          value: email,
          type: 'groups',
          data: { name: email, mail: email, email },
        }));
        setSelectedEmails(convertedEmails);
      }
    }
  }, [course, isOpen]);

  const handleCreateQuest = (formikObject) => {
    Object.entries(formikObject).forEach(([key, value]) => {
      setCreateFormInputs((prevValues) => ({
        ...prevValues,
        [key]: value,
      }));
    });

    // Convert selectedEmails to simple email strings for backend compatibility
    const emails = selectedEmails.map((item) => item.value);

    dispatch(
      createAddCourseQuestAction({
        courseId: id,
        name: formikObject.name,
        description: formikObject.description,
        ownerGroups: emails,
      })
    );
  };

  const handleResultButton = () => {
    dispatch(questActions.resetCreateStatus());
    if (createAddQuest.status === ReducerStatus.SUCCEEDED) {
      setCreateFormInputs({
        name: '',
        description: null,
      });
      setSelectedEmails([]);
      onClose();
    }
  };

  return (
    <Box>
      {createAddQuest.status === ReducerStatus.SUCCEEDED ||
      createAddQuest.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={isOpen}
          onSubmit={handleResultButton}
          isError={createAddQuest.status === ReducerStatus.FAILED}
          title={
            createAddQuest.status === ReducerStatus.SUCCEEDED
              ? 'Quest Created'
              : null
          }
          message={
            createAddQuest.status === ReducerStatus.SUCCEEDED
              ? 'The quest has been created successfully.'
              : createAddQuest.error?.data?.title || 'Server Error'
          }
          errorObject={createAddQuest.error?.data?.errors}
        />
      ) : (
        <>
          <Button
            onClick={onOpen}
            leftIcon={<FaPlus />}
            colorScheme="teal"
            variant="solid"
          >
            Add A New Quest
          </Button>
          <Modal size="2xl" isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Create Quest</ModalHeader>
              <ModalCloseButton />
              <Formik
                enableReinitialize={false}
                initialValues={createFormInputs}
                onSubmit={(values) => {
                  handleCreateQuest(values);
                }}
                validationSchema={questFormValidation}
              >
                {() => (
                  <Form>
                    <ModalBody my={3}>
                      <VStack spacing={6} align="stretch">
                        <Flex
                          gap={5}
                          direction={{
                            base: 'column',
                            md: 'row',
                          }}
                        >
                          <Field flex={1} name="name">
                            {({ field, form }) => (
                              <FormikFormControl
                                label="Name *"
                                helperText="Enter the name of the quest. Make it concise and descriptive."
                                errors={form.errors.name}
                                form={form}
                                touched={form.touched.name}
                              >
                                <Input {...field} />
                              </FormikFormControl>
                            )}
                          </Field>
                        </Flex>

                        <Field name="description">
                          {({ field, form }) => (
                            <FormikFormControl
                              label="Description"
                              helperText="Provide a detailed description of the quest. Mention the key objectives and what learners can expect to gain."
                              errors={form.errors.description}
                              touched={form.touched.description}
                            >
                              <Textarea {...field} />
                            </FormikFormControl>
                          )}
                        </Field>

                        <MultiEmailInput
                          label="Owner Groups"
                          helperText="Search and select users or distribution groups that have the authority to modify this quest. You can switch between searching users and groups using the dropdown."
                          selectedItems={selectedEmails}
                          isError={false}
                          errorMessage=""
                          onItemAdd={handleEmailAdd}
                          onItemRemove={handleEmailRemove}
                          defaultSearchType="groups"
                        />
                      </VStack>
                    </ModalBody>

                    <ModalFooter>
                      <Button
                        variant="outline"
                        colorScheme="blue"
                        mr={3}
                        onClick={() => onClose}
                      >
                        Close
                      </Button>
                      <Button
                        colorScheme="red"
                        px={10}
                        type="submit"
                        isLoading={
                          createAddQuest.status === ReducerStatus.LOADING
                        }
                      >
                        Create
                      </Button>
                    </ModalFooter>
                  </Form>
                )}
              </Formik>
            </ModalContent>
          </Modal>
        </>
      )}
    </Box>
  );
}

CreateQuestMd.propTypes = {
  id: PropTypes.number.isRequired,
};

export default CreateQuestMd;
